import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import { browserTimezone } from "../../lib/time";
import type { Profile } from "../../lib/database.types";

interface AuthState {
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  userId: string | null;
}

const AuthContext = createContext<AuthState>({
  session: null,
  profile: null,
  loading: true,
  userId: null,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange(
      (event, next) => {
        setSession(next);
        setLoading(false);
        if (event === "SIGNED_OUT") {
          setProfile(null);
          queryClient.clear();
        }
      },
    );

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [queryClient]);

  const userId = session?.user.id ?? null;

  useEffect(() => {
    if (!userId) return;
    let active = true;

    (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (!active || error || !data) return;
      setProfile(data);

      // Keep the profile's timezone in step with the phone, so the 8pm nudge
      // stays at 8pm after a flight.
      const zone = browserTimezone();
      if (data.timezone !== zone) {
        const { data: updated } = await supabase
          .from("profiles")
          .update({ timezone: zone })
          .eq("id", userId)
          .select()
          .maybeSingle();
        if (active && updated) setProfile(updated);
      }
    })();

    return () => {
      active = false;
    };
  }, [userId]);

  const value = useMemo<AuthState>(
    () => ({ session, profile, loading, userId }),
    [session, profile, loading, userId],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  return useContext(AuthContext);
}

/** For the many places that simply cannot render without a signed-in user. */
// eslint-disable-next-line react-refresh/only-export-components
export function useUserId(): string {
  const { userId } = useAuth();
  if (!userId)
    throw new Error("useUserId called outside an authenticated route");
  return userId;
}
